/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useMemo } from 'react';
import { FormattedMessage } from 'react-intl';

import moment from 'moment';

import { Typography2 } from '@coursera/cds-core';

import type { SourceCoursePartners } from 'bundles/common/components/EmbeddedSourceCourseAttribution';
import EmbeddedSourceCourseAttribution from 'bundles/common/components/EmbeddedSourceCourseAttribution';
import { TrackedA } from 'bundles/page/components/TrackedLink2';
import DottedSeparator from 'bundles/unified-description-page-common/components/shared/DottedSeparator';
import Subsection from 'bundles/unified-description-page-common/components/shared/Subsection';
import type { WeeklyMaterialItemFragment } from 'bundles/unified-description-page-common/queries/bff/__generated__/SharedCourseEntitiesFragments';
import { durationToMinutes } from 'bundles/unified-description-page-common/utils/learningTime';

import _t from 'i18n!nls/xdp';

type Props = {
  groupLabel: React.ReactNode;
  items?: WeeklyMaterialItemFragment[] | null;
  showPreviewLink?: boolean;
  courseSlug: string;
  authoringPartnerID?: string;
};

const styles = {
  title: css`
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  `,
  items: css`
    margin-bottom: 0;
  `,
  item: css`
    & + & {
      padding-top: var(--cds-spacing-200);
    }
  `,
  itemDuration: css`
    margin-right: var(--cds-spacing-100);
  `,
  link: css`
    color: var(--cds-color-interactive-primary);
    font-weight: 600;

    :hover {
      text-decoration: underline;
      color: var(--cds-color-interactive-primary-hover);
    }
  `,
  partnerLogosContainer: css`
    display: inline-flex;
    vertical-align: middle;
  `,
};

const ModuleMaterialGroup = ({ groupLabel, items, showPreviewLink, courseSlug, authoringPartnerID }: Props) => {
  const learningTime = useMemo(() => {
    if (!items?.length) {
      return 0;
    }

    return Math.round(
      items.reduce((previousValue, item) => previousValue + moment.duration(item.duration).asMinutes(), 0)
    );
  }, [items]);

  if (!items?.length) {
    return null;
  }

  return (
    <Subsection
      title={
        <Typography2 variant="subtitleMedium" component="h5" css={styles.title}>
          {groupLabel}
          {Boolean(learningTime) && (
            <React.Fragment>
              <DottedSeparator />
              <FormattedMessage
                message={_t('Total {minutes, plural, =1 {# minute} other {# minutes}}')}
                minutes={learningTime}
              />
            </React.Fragment>
          )}
        </Typography2>
      }
    >
      {items && items.length > 0 && (
        <ul css={styles.items}>
          {items.map((item, index) => {
            return (
              <Typography2 variant="bodyPrimary" component="li" css={styles.item} key={item.id}>
                {item.name}
                <DottedSeparator />
                {item.duration && (
                  <Typography2 variant="bodyPrimary" component="span" color="supportText" css={styles.itemDuration}>
                    {durationToMinutes(item.duration)}
                  </Typography2>
                )}
                {index === 0 && showPreviewLink && item.slug && (
                  <span>
                    <DottedSeparator />
                    <TrackedA
                      css={styles.link}
                      href={`/lecture/${courseSlug}/${item.slug}-${item.id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      trackingName="syllabus_item_link"
                      data={{
                        name: item.name,
                      }}
                    >
                      {_t('Preview module')}
                    </TrackedA>
                  </span>
                )}
                {item.partners && authoringPartnerID && (
                  <Typography2
                    variant="bodyPrimary"
                    component="span"
                    color="supportText"
                    css={styles.partnerLogosContainer}
                  >
                    <EmbeddedSourceCourseAttribution
                      embeddedContentSourceCourseId={item.id}
                      partners={
                        item.partners.filter((partner) => partner.id !== authoringPartnerID) as SourceCoursePartners
                      }
                    />
                  </Typography2>
                )}
              </Typography2>
            );
          })}
        </ul>
      )}
    </Subsection>
  );
};

export default ModuleMaterialGroup;
